import { useEffect, useState } from 'react';
import { Bell, X, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { isPushSupported, getDeviceSubscription, subscribeWebPush } from '@/lib/webpush';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * Per-device push prompt.
 * Web Push subscriptions are per-device. A user can enable push on one device
 * (setting the account flag) but a NEW device won't have its own subscription and
 * won't have been asked for notification permission. This banner detects that case
 * and lets the user enable push on the current device.
 */
export default function PushDevicePrompt() {
  const { user, setUser } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) { setShow(false); return; }
      if (!(await isPushSupported())) return;
      if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return;

      // iOS Safari (not installed) is handled by IosPushHelp instead.
      const ua = navigator.userAgent || '';
      const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
      const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
      if (isIos && !isStandalone) return;

      const dismissed = localStorage.getItem('ptt_push_device_dismissed');
      if (dismissed && Date.now() - parseInt(dismissed, 10) < 3 * 24 * 60 * 60 * 1000) return;

      const sub = await getDeviceSubscription();
      // Show only when the account WANTS push but THIS device has no subscription
      // (e.g. push was set up on another device). Avoids nagging users who disabled push.
      if (!sub && user?.channels?.webpush && !cancelled) setShow(true);
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!show || !user) return null;

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeWebPush();
      try { const { data } = await api.get('/auth/me'); setUser(data); localStorage.setItem('ptt_user', JSON.stringify(data)); } catch {}
      toast.success("Push enabled on this device — you'll get alerts even when the app is closed");
      setShow(false);
    } catch (e) {
      toast.error(e.message || 'Could not enable push on this device');
    } finally { setBusy(false); }
  };

  const dismiss = () => {
    localStorage.setItem('ptt_push_device_dismissed', String(Date.now()));
    setShow(false);
  };

  const accountOn = user?.channels?.webpush;

  return (
    <div className="fixed inset-x-3 bottom-3 sm:right-4 sm:left-auto sm:max-w-sm z-50 bg-white rounded-2xl shadow-2xl border border-orange-200 overflow-hidden" data-testid="push-device-prompt">
      <div className="p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-orange-500/15 text-orange-600 flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm">{accountOn ? 'Turn on alerts for this device' : 'Get push alerts on this device'}</div>
          <div className="text-xs text-zinc-600 mt-0.5">
            {accountOn
              ? 'Push is enabled on your account but not on this device yet. Enable it here so you get alerts on this phone/computer too.'
              : "Allow notifications to get road alerts even when the app is closed."}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button onClick={enable} disabled={busy} className="btn btn-primary !px-3 !py-1.5 text-xs inline-flex items-center gap-1" data-testid="push-device-enable">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enable notifications'}
            </button>
            <button onClick={dismiss} className="text-xs text-zinc-500 hover:text-zinc-800 px-2" data-testid="push-device-dismiss">Not now</button>
          </div>
        </div>
        <button onClick={dismiss} className="text-zinc-400 hover:text-zinc-700 flex-shrink-0" aria-label="Dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
